/**
 * usePhase1CapableMachines.ts — capable-machine union for the Phase-1 machine
 * grid. Extracted verbatim from Phase1MachineGrid.tsx (600-line cap,
 * CODING-STANDARDS) while adding the extendable date window (BUG-2026-0178).
 *
 *   - capableMachines      — union of every machine capable of ANY task type in
 *                            the current task list (rows of the grid). Uses
 *                            `listCapableMachines(taskTypeKey)` — same data
 *                            source as `MachineSelectCell` — deduped by MachineKey.
 *                            Refetched only when the SET of task types changes.
 *   - capabilityByTaskType — taskTypeKey → capable MachineKeys (drag drop-target
 *                            constraint), seeded from the per-task-type loads and
 *                            merged with the global capability map fetched once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMachineCapabilities } from '../../../../services/scheduleBookings';
import { listCapableMachines } from '../../../../services/scheduleTasks';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';
import type { LabSection } from '../../../../types/schedule';
import type { MachineSummary } from '../../../../hooks/useScheduleData';

export interface Phase1CapableMachines {
  capableMachines: MachineSummary[];
  capabilityByTaskType: Map<string, Set<string>>;
}

export function usePhase1CapableMachines(tasks: TaskWithBookings[]): Phase1CapableMachines {
  const [capableMachines, setCapableMachines] = useState<MachineSummary[]>([]);
  const [capabilityByTaskType, setCapabilityByTaskType] = useState<Map<string, Set<string>>>(
    () => new Map(),
  );

  // Track the last task-type set so we only refetch capabilities when it changes.
  const prevTaskTypeKeysRef = useRef<string>('');

  const loadCapableMachines = useCallback(async (taskList: TaskWithBookings[]) => {
    const uniqueKeys = [...new Set(
      taskList.map((t) => t.TaskTypeKey).filter((k): k is string => k !== null),
    )];

    if (uniqueKeys.length === 0) {
      setCapableMachines([]);
      setCapabilityByTaskType(new Map());
      return;
    }

    // Deduplicate by MachineKey across all task types
    const machineMap = new Map<string, MachineSummary>();
    const capMap = new Map<string, Set<string>>();

    await Promise.all(
      uniqueKeys.map(async (key) => {
        try {
          const rows = await listCapableMachines(key);
          const set = capMap.get(key) ?? new Set<string>();
          for (const r of rows) {
            set.add(r.MachineKey);
            if (!machineMap.has(r.MachineKey)) {
              machineMap.set(r.MachineKey, {
                machineKey: r.MachineKey,
                code: r.Code,
                name: r.Name,
                labSection: (r.LabSection as LabSection) ?? null,
                machineGroup: (r as { MachineGroup?: string | null }).MachineGroup ?? null,
              });
            }
          }
          capMap.set(key, set);
        } catch {
          // Best-effort; a failed taskType just contributes no machines.
        }
      }),
    );

    setCapableMachines([...machineMap.values()]);
    setCapabilityByTaskType(capMap);
  }, []);

  useEffect(() => {
    const keyStr = tasks
      .map((t) => t.TaskTypeKey ?? '')
      .sort()
      .join(',');
    if (keyStr === prevTaskTypeKeysRef.current) return;
    prevTaskTypeKeysRef.current = keyStr;
    void loadCapableMachines(tasks);
  }, [tasks, loadCapableMachines]);

  // Also fetch machine capabilities once
  useEffect(() => {
    fetchMachineCapabilities()
      .then((rows) => {
        const map = new Map<string, Set<string>>();
        for (const r of rows) {
          if (!r.TaskTypeKey) continue;
          const set = map.get(r.TaskTypeKey) ?? new Set<string>();
          set.add(r.MachineKey);
          map.set(r.TaskTypeKey, set);
        }
        // Merge into the task-type map (the load above may already have it)
        setCapabilityByTaskType((prev) => {
          const merged = new Map(prev);
          for (const [k, v] of map) {
            const existing = merged.get(k) ?? new Set<string>();
            for (const mk of v) existing.add(mk);
            merged.set(k, existing);
          }
          return merged;
        });
      })
      .catch(() => {
        // Capability map enhancement — failure just means no drop constraint.
      });
  // Run once on mount.
  }, []);

  return { capableMachines, capabilityByTaskType };
}
